import dayjs from 'dayjs'
import { Note, isCalendarNote, filenameToDate } from '../../../utils/note'
import { TaskItem } from '../../../modules/filter/types'

// Helper function to determine if task source note should be shown
export function shouldShowTaskSourceNote(
  task: TaskItem,
  contextNote?: Note
): boolean {
  const { scheduledDate, sourceNote } = task

  // If we have a context note (showing tasks within their source note),
  // only show source if it's different from the context
  if (contextNote) {
    // Don't show source if task is from the same note we're viewing
    if (
      sourceNote.recordName === contextNote.recordName ||
      sourceNote.filename === contextNote.filename
    ) {
      return false
    }
    // If it's a different note, show the source
    return true
  }

  // For individual tasks (no context note), determine based on note type and scheduling

  // For project notes, always show source when task is displayed individually
  if (!isCalendarNote(sourceNote.noteType)) {
    return true
  }

  // For calendar notes, show source if task is scheduled for a different date
  if (scheduledDate && scheduledDate !== 'today') {
    const { date: noteDate } = filenameToDate(sourceNote.filename)
    if (noteDate) {
      const noteDateString = noteDate.format('YYYY-MM-DD')
      return scheduledDate !== noteDateString
    }
  }

  // If scheduled for 'today', check if note is today's note
  if (scheduledDate === 'today') {
    const { date: noteDate } = filenameToDate(sourceNote.filename)
    if (noteDate) {
      const today = dayjs()
      return !noteDate.isSame(today, 'day')
    }
  }

  return false
}

/**
 * Reconstructs the full task line format from a TaskItem
 * This is needed because TaskItem.content only contains the text after the bullet and state markers,
 * but BlockNoteEditor.notePlanToBlocks() expects the full format including markers.
 */
export function reconstructTaskLine(task: TaskItem): string {
  const { bullet, state, content } = task

  // For scheduled tasks (state '>'), add the [>] marker
  if (state === '>') {
    return `${bullet} [>] ${content}`
  }

  // For open tasks/checklists, check if content already includes [ ] marker
  // This handles both formats: "* test" and "* [ ] test"
  if (state === ' ') {
    // Content already has [ ] marker, use as-is; otherwise add it
    return content.startsWith('[ ] ')
      ? `${bullet} ${content}`
      : `${bullet} [ ] ${content}`
  }

  // For completed/cancelled tasks, always add the appropriate marker
  let stateMarker = ''
  switch (state) {
    case 'x': {
      stateMarker = '[x] '
      break
    }
    case '-': {
      stateMarker = '[-] '
      break
    }
    default: {
      stateMarker = '[ ] '
      break
    }
  }

  // Reconstruct the full line: bullet + space + state + content
  return `${bullet} ${stateMarker}${content}`
}
